import React, { useContext, useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import "../../../assets/css/gcstudent/whatpeoplesay/WhatPeopleSay.css";
import AppContext from "../../../context/AppContext";

/* ── Star Rating Display ── */
const StarRating = ({ rating }) => (
  <div className="gcStudent-wps-stars">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        width="13" height="13" viewBox="0 0 24 24"
        fill={star <= rating ? "#f59e0b" : "none"}
        stroke={star <= rating ? "#f59e0b" : "#d1d5db"}
        strokeWidth="1.5"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ))}
  </div>
);

/* ── Avatar Initials fallback ── */
const AvatarInitials = ({ name }) => {
  const initials = name
    ? name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : "?";
  return (
    <div className="gcStudent-wps-avatar-initials">
      <span>{initials}</span>
    </div>
  );
};

const PAGE_SIZE = 4;

const WhatPeopleSay = () => {
  const { baseUrl }       = useContext(AppContext);
  const [reviews,     setReviews]     = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading,     setLoading]     = useState(true);
  const [isVisible,   setIsVisible]   = useState(false);
  const sectionRef = useRef(null);

  /* ── Intersection Observer ── */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  /* ── Fetch approved reviews, sort by rating desc ── */
  useEffect(() => {
      axios.get(`${baseUrl}/home/loadReviews`)
      .then((res) => {
        setLoading(false);
        if (res.data.success === true) {
          setReviews(res.data.reviews || []);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error("Reviews fetch error:", err);
      });
  }, [baseUrl]);

  const visibleReviews = reviews.slice(0, visibleCount);
  const hasMore        = visibleCount < reviews.length;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  };

  return (
    <section className="gcStudent-wps-section" ref={sectionRef}>
      <Container>

        {/* Header */}
        <div className={`gcStudent-wps-header ${isVisible ? "gcStudent-wps-header--visible" : ""}`}>
          <h2 className="gcStudent-wps-title">What People Say</h2>
          <p className="gcStudent-wps-subtitle">
            Hear from students and staff who use our platform
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="gcStudent-wps-loading">
            <div className="gcStudent-wps-loading-dots">
              <span /><span /><span />
            </div>
          </div>
        )}

        {/* Cards Grid */}
        {!loading && visibleReviews.length > 0 && (
          <Row className="gcStudent-wps-row">
            {visibleReviews.map((item, index) => {
              const name = item.reviewer_name || item.teacher?.name || "Anonymous";
              const role = item.reviewer_role || (item.teacher ? "Staff" : "Student");
              const text = item.review || item.comment || "";
              const rating = item.rating || 5;

              return (
                <Col
                  key={item.id}
                  xs={12} sm={6} lg={3}
                  className="gcStudent-wps-col"
                >
                  <div
                    className={`gcStudent-wps-card ${isVisible ? "gcStudent-wps-card--visible" : ""}`}
                    style={{ animationDelay: `${0.08 + (index % PAGE_SIZE) * 0.1}s` }}
                  >
                    {/* Quote mark */}
                    <div className="gcStudent-wps-quote-mark">&ldquo;</div>

                    {/* Stars */}
                    <StarRating rating={rating} />

                    {/* Quote text */}
                    <p className="gcStudent-wps-quote-text">
                      &ldquo;{text}&rdquo;
                    </p>

                    {/* Author */}
                    <div className="gcStudent-wps-author">
                      <div className="gcStudent-wps-avatar-wrapper">
                        <AvatarInitials name={name} />
                      </div>
                      <div className="gcStudent-wps-author-info">
                        <span className="gcStudent-wps-author-name">{name}</span>
                        <span className="gcStudent-wps-author-role">{role}</span>
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}

        {/* Empty state */}
        {!loading && visibleReviews.length === 0 && (
          <div className="gcStudent-wps-empty">
            <p>No reviews yet. Be the first to share your experience!</p>
          </div>
        )}

        {/* Load More Button */}
        {!loading && hasMore && (
          <div className="gcStudent-wps-loadmore-wrap">
            <button
              className="gcStudent-wps-loadmore-btn"
              onClick={handleLoadMore}
            >
              Load More
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        )}

      </Container>
    </section>
  );
};

export default WhatPeopleSay;